import { Button, Paper, TextInput, FileInput, Progress, Stack, Group } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { Copy, Upload as UploadIcon } from "lucide-react";
import React, { useState } from "react";
import Head from "../components/common/Head";
import useValidate from "../hooks/useValidate";
import { URL } from "../utils/api";

const UploadLink = () => {
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);

  useValidate();

  const handleFileChange = (file) => {
    setSelectedFile(file);
    setUploadedUrl("");
    setUploadProgress(0);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      notifications.show({ message: "Please select a file first", color: "yellow" });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadedUrl("");

    const formData = new FormData();
    formData.append("file", selectedFile);

    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = (event.loaded / event.total) * 100;
        setUploadProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        try {
          const response = JSON.parse(xhr.response);
          const url = Array.isArray(response) ? response[0] : response.url || response;
          setUploadedUrl(url);
          notifications.show({ message: "File uploaded successfully!", color: "green" });
        } catch {
          notifications.show({ message: "Failed to parse upload response", color: "red" });
        }
      } else {
        notifications.show({ message: "Upload failed", color: "red" });
      }
      setUploading(false);
      setUploadProgress(0);
    };

    xhr.onerror = () => {
      notifications.show({ message: "Upload failed", color: "red" });
      setUploading(false);
      setUploadProgress(0);
    };

    xhr.open("POST", `${URL}/upload`);
    const token = localStorage.getItem("tburudani_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    xhr.send(formData);
  };

  const handleCopyLink = () => {
    if (!uploadedUrl) {
      notifications.show({ message: "No link to copy", color: "yellow" });
      return;
    }
    navigator.clipboard
      .writeText(uploadedUrl)
      .then(() => notifications.show({ message: "Link copied to clipboard!", color: "green" }))
      .catch(() => notifications.show({ message: "Failed to copy link", color: "red" }));
  };

  return (
    <div className="flex flex-col gap-4">
      <Head title="Upload Link" />

      <Paper withBorder p="md" className="bg-zinc-900 border-zinc-800">
        <Stack gap="md">
          <div>
            <h3 className="text-white text-lg font-semibold mb-2">Upload Image or Video</h3>
            <p className="text-gray-400 text-sm">Upload an image or video file to get a shareable link</p>
          </div>

          <Group align="flex-end">
            <FileInput
              label="Select File"
              placeholder="Choose image or video..."
              accept="image/*,video/*"
              value={selectedFile}
              onChange={handleFileChange}
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<UploadIcon size={16} />}
              loading={uploading}
              disabled={!selectedFile || uploading}
              onClick={handleUpload}
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </Group>

          {uploading && (
            <Progress value={uploadProgress} size="sm" striped animated />
          )}

          {uploadedUrl && (
            <Stack gap="sm">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Uploaded Link:</label>
                <Group>
                  <TextInput value={uploadedUrl} readOnly style={{ flex: 1 }} />
                  <Button leftSection={<Copy size={16} />} onClick={handleCopyLink}>
                    Copy Link
                  </Button>
                </Group>
              </div>

              <div>
                <label className="text-white text-sm font-medium mb-2 block">Preview:</label>
                <div className="border border-zinc-700 rounded-lg p-4 bg-zinc-950">
                  {uploadedUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                    <img src={uploadedUrl} alt="Uploaded" className="max-w-full h-auto max-h-96 rounded" />
                  ) : uploadedUrl.match(/\.(mp4|webm|ogg)$/i) ? (
                    <video src={uploadedUrl} controls className="max-w-full h-auto max-h-96 rounded">
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className="text-gray-400 text-sm">Preview not available for this file type</div>
                  )}
                </div>
              </div>
            </Stack>
          )}
        </Stack>
      </Paper>
    </div>
  );
};

export default UploadLink;
